import { gsap, Linear } from 'gsap';
import lottie from 'lottie-web';
import animationJson from '@/assets/animations/ida-animation.json';

import { onMounted, ref, computed } from 'vue';
import type { HeroData } from '@/models/hero';

import logoImg from '@/assets/images/logo-magenta.svg';
import clientImg from '@/assets/images/hero/client.jpg';
import bulbImg from '@/assets/images/hero/bulb.png';
import cartImg from '@/assets/images/hero/cart.png';
import emailImg from '@/assets/images/hero/email.png';
import diagramImg from '@/assets/images/hero/diagram.jpg';
import chartImg from '@/assets/images/hero/chart.png';
import requestImg from '@/assets/images/hero/request.png';
import { convertMultilineContent } from '~/utils/content-utils';

export const useHero = (heroData: HeroData) => {
  const animationRef = ref<HTMLElement | null>(null);

  const animationLoaded = ref(false);

  const mappedMessages = computed(() => {
    return [
      {
        id: 'idaMessage1',
        classes: ['ida', 'br', 'white'],
        title: convertMultilineContent(heroData.aiMessages[0] ?? undefined),
        image: logoImg
      },
      {
        id: 'idaMessage2',
        classes: ['ida', 'br', 'white'],
        title: convertMultilineContent(heroData.aiMessages[1] ?? undefined),
        image: logoImg
      },
      {
        id: 'idaMessage3',
        classes: ['ida', 'br', 'white'],
        title: convertMultilineContent(heroData.aiMessages[2] ?? undefined),
        image: logoImg
      },
      {
        id: 'idaMessage4',
        classes: ['ida', 'br', 'white'],
        title: convertMultilineContent(heroData.aiMessages[3] ?? undefined),
        image: logoImg
      },
      {
        id: 'idaMessage5',
        classes: ['ida', 'br', 'white'],
        title: convertMultilineContent(heroData.aiMessages[4] ?? undefined),
        image: logoImg
      },
      {
        id: 'idaMessage6',
        classes: ['ida', 'br', 'white'],
        title: convertMultilineContent(heroData.aiMessages[5] ?? undefined),
        image: logoImg
      },
      {
        id: 'idaMessage7',
        classes: ['ida', 'br', 'white'],
        title: convertMultilineContent(heroData.aiMessages[6] ?? undefined),
        image: logoImg
      },
      {
        id: 'idaMessage8',
        classes: ['ida', 'br', 'white'],
        title: convertMultilineContent(heroData.aiMessages[7] ?? undefined),
        image: logoImg
      },
      {
        id: 'clientMessage1',
        classes: ['client', 'tl', 'avatar'],
        title: convertMultilineContent(heroData.clientMessages[0] ?? undefined),
        image: clientImg
      },
      {
        id: 'clientMessage2',
        classes: ['client', 'tl', 'avatar'],
        title: convertMultilineContent(heroData.clientMessages[1] ?? undefined),
        image: clientImg
      },
      {
        id: 'clientMessage3',
        classes: ['client', 'tl', 'avatar'],
        title: convertMultilineContent(heroData.clientMessages[2] ?? undefined),
        image: clientImg
      },
      {
        id: 'clientMessage4',
        classes: ['client', 'tl', 'avatar'],
        title: convertMultilineContent(heroData.clientMessages[3] ?? undefined),
        image: clientImg
      },
      {
        id: 'imageBulb',
        classes: ['icon', 'ida', 'br', 'z0'],
        image: bulbImg
      },
      {
        id: 'imageCart',
        classes: ['icon', 'br', 'z0'],
        image: cartImg
      },
      {
        id: 'imageEmail',
        classes: ['icon', 'ida', 'br', 'z0'],
        image: emailImg
      },
      {
        id: 'diagramMessage1',
        classes: ['diagram', 'bl'],
        title: convertMultilineContent(heroData.aiMessages[8] ?? undefined),
        image: diagramImg
      },
      {
        id: 'diagramMessage2',
        classes: ['ida', 'half', 'bl'],
        title: convertMultilineContent(heroData.aiMessages[9] ?? undefined),
        image: chartImg
      },
      {
        id: 'crMessage',
        classes: ['ida', 'half', 'bl'],
        title: convertMultilineContent(heroData.aiMessages[10] ?? undefined),
        image: requestImg
      }
    ];
  });

  const initBackgroundAnimation = () => {
    if (!animationRef.value) return;

    lottie.loadAnimation({
      container: animationRef.value,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: animationJson
      //path: '/ida-animation.json'
    });
  };

  const initMessagesAnimation = () => {
    const cards = gsap.utils.toArray('.hero-card');

    const idaMessage1 = document.querySelector('#idaMessage1');
    const idaMessage2 = document.querySelector('#idaMessage2');
    const idaMessage3 = document.querySelector('#idaMessage3');
    const idaMessage4 = document.querySelector('#idaMessage4');
    const idaMessage5 = document.querySelector('#idaMessage5');
    const idaMessage6 = document.querySelector('#idaMessage6');
    const idaMessage7 = document.querySelector('#idaMessage7');
    const idaMessage8 = document.querySelector('#idaMessage8');
    const clientMessage1 = document.querySelector('#clientMessage1');
    const clientMessage2 = document.querySelector('#clientMessage2');
    const clientMessage3 = document.querySelector('#clientMessage3');
    const clientMessage4 = document.querySelector('#clientMessage4');
    const imageBulb = document.querySelector('#imageBulb');
    const imageCart = document.querySelector('#imageCart');
    const imageEmail = document.querySelector('#imageEmail');
    const diagram1 = document.querySelector('#diagramMessage1');
    const diagram2 = document.querySelector('#diagramMessage2');
    const customerRequest = document.querySelector('#crMessage');

    gsap.set(cards, {
      opacity: 0
    });
    gsap.set(idaMessage1, {
      transform: 'translate(-200px, 0)'
    });
    gsap.set(idaMessage2, {
      transform: 'translate(-50px, 0)'
    });
    gsap.set(idaMessage3, {
      transform: 'translate(-50px, 0)'
    });
    gsap.set(idaMessage4, {
      transform: 'translate(-50px, 0)'
    });
    gsap.set(idaMessage5, {
      transform: 'translate(-50px, 0)'
    });
    gsap.set(idaMessage6, {
      transform: 'translate(-50px, 0)'
    });
    gsap.set(idaMessage7, {
      transform: 'translate(-50px, 0)'
    });
    gsap.set(idaMessage8, {
      transform: 'translate(-50px, 0)'
    });
    gsap.set(clientMessage1, {
      transform: 'translate(50px, 0)'
    });
    gsap.set(clientMessage2, {
      transform: 'translate(50px, 0)'
    });
    gsap.set(clientMessage3, {
      transform: 'translate(50px, 0)'
    });
    gsap.set(clientMessage4, {
      transform: 'translate(50px, 0)'
    });
    gsap.set(imageBulb, {
      transform: 'translate(-50px, 0)'
    });
    gsap.set(imageCart, {
      transform: 'translate(-50px, 0)'
    });
    gsap.set(imageEmail, {
      transform: 'translate(-50px, 0)'
    });
    gsap.set(diagram1, {
      scale: 0,
      transform: 'translate(0, -50px)'
    });
    gsap.set(diagram2, {
      scale: 0,
      transform: 'translate(0, -50px)'
    });
    gsap.set(customerRequest, {
      scale: 0,
      transform: 'translate(0, -50px)'
    });

    const timeline = gsap.timeline({
      onComplete: function () {
        this.restart();
      }
    });

    timeline
      .to(
        idaMessage1,
        {
          opacity: 1,
          transform: 'translate(0, 0)',
          duration: 1.5,
          ease: 'power2.out'
        },
        1.5
      )
      .to(
        imageBulb,
        {
          opacity: 1,
          transform: 'translate(-50px, -100px)',
          duration: 1,
          ease: Linear.easeIn
        },
        '>-=0.250'
      )
      .to(
        imageBulb,
        {
          opacity: 0,
          transform: 'translate(-50px, -200px)',
          duration: 1,
          ease: Linear.easeIn
        },
        '>'
      )
      .to(idaMessage1, {
        opacity: 0,
        transform: 'translate(50px, 0)',
        duration: 0.5,
        ease: 'power2.out'
      })
      .to(
        idaMessage2,
        {
          opacity: 1,
          transform: 'translate(0, 0)',
          duration: 0.5,
          ease: 'power2.out'
        },
        '>'
      )
      .to(
        idaMessage2,
        {
          opacity: 0,
          transform: 'translate(50px, 0)',
          duration: 0.5,
          ease: 'power2.out'
        },
        '>+=2.5'
      )
      .to(idaMessage3, {
        opacity: 1,
        transform: 'translate(0, 0)',
        duration: 0.5,
        ease: 'power2.out'
      })
      .to(
        imageCart,
        {
          opacity: 1,
          transform: 'translate(-50px, -100px)',
          duration: 1,
          ease: Linear.easeIn
        },
        '>-=0.250'
      )
      .to(
        imageCart,
        {
          opacity: 0,
          transform: 'translate(-50px, -200px)',
          duration: 1,
          ease: Linear.easeIn
        },
        '>'
      )
      .to(
        clientMessage1,
        {
          opacity: 1,
          transform: 'translate(0, 0)',
          duration: 0.5,
          ease: 'power2.out'
        },
        '>-=0.5'
      )
      .to(
        idaMessage3,
        {
          opacity: 0,
          transform: 'translate(50px, 0)',
          duration: 0.5,
          ease: 'power2.out'
        },
        '>'
      )
      .to(idaMessage4, {
        opacity: 1,
        transform: 'translate(0, 0)',
        duration: 0.5,
        ease: 'power2.out'
      })
      .to(diagram1, {
        opacity: 1,
        scale: 1,
        duration: 0.5,
        ease: 'power2.out'
      })
      .to(
        clientMessage1,
        {
          opacity: 0,
          transform: 'translate(-50px, 0)',
          duration: 0.5,
          ease: 'power2.out'
        },
        '>+=2'
      )
      .to(
        clientMessage2,
        {
          opacity: 1,
          transform: 'translate(0, 0)',
          duration: 0.5,
          ease: 'power2.out'
        },
        '>-=.25'
      )
      .to(
        diagram1,
        {
          opacity: 0,
          scale: 0,
          duration: 0.5,
          ease: 'power2.out'
        },
        '>+=2'
      )
      .to(
        idaMessage4,
        {
          opacity: 0,
          transform: 'translate(50px, 0)',
          duration: 0.5,
          ease: 'power2.out'
        },
        '<'
      )
      .to(idaMessage5, {
        opacity: 1,
        transform: 'translate(0, 0)',
        duration: 0.5,
        ease: 'power2.out'
      })
      .to(
        diagram2,
        {
          opacity: 1,
          scale: 1,
          duration: 0.5,
          ease: 'power2.out'
        },
        '>+=2'
      )
      .to(
        clientMessage2,
        {
          opacity: 0,
          transform: 'translate(-50px, 0)',
          duration: 0.5,
          ease: 'power2.out'
        },
        '>+=2'
      )
      .to(
        clientMessage3,
        {
          opacity: 1,
          transform: 'translate(0, 0)',
          duration: 0.5,
          ease: 'power2.out'
        },
        '>-=.25'
      )
      .to(
        diagram2,
        {
          opacity: 0,
          scale: 0,
          duration: 0.5,
          ease: 'power2.out'
        },
        '>+=2'
      )
      .to(
        idaMessage5,
        {
          opacity: 0,
          transform: 'translate(50px, 0)',
          duration: 0.5,
          ease: 'power2.out'
        },
        '<'
      )
      .to(idaMessage6, {
        opacity: 1,
        transform: 'translate(0, 0)',
        duration: 0.5,
        ease: 'power2.out'
      })
      .to(
        imageEmail,
        {
          opacity: 1,
          transform: 'translate(-50px, -100px)',
          duration: 1,
          ease: Linear.easeIn
        },
        '>-=0.250'
      )
      .to(
        imageEmail,
        {
          opacity: 0,
          transform: 'translate(-50px, -200px)',
          duration: 1,
          ease: Linear.easeIn
        },
        '>'
      )
      .to(idaMessage6, {
        opacity: 0,
        transform: 'translate(50px, 0)',
        duration: 0.5,
        ease: 'power2.out'
      })
      .to(
        clientMessage3,
        {
          opacity: 0,
          transform: 'translate(-50px, 0)',
          duration: 0.5,
          ease: 'power2.out'
        },
        '>'
      )
      .to(
        clientMessage4,
        {
          opacity: 1,
          transform: 'translate(0, 0)',
          duration: 0.5,
          ease: 'power2.out'
        },
        '>-=.25'
      )
      .to(
        idaMessage7,
        {
          opacity: 1,
          transform: 'translate(0, 0)',
          duration: 0.5,
          ease: 'power2.out'
        },
        '>+=1'
      )
      .to(
        customerRequest,
        {
          opacity: 1,
          scale: 1,
          duration: 0.5,
          ease: 'power2.out'
        },
        '<'
      )
      .to(
        customerRequest,
        {
          opacity: 0,
          scale: 0,
          duration: 0.5,
          ease: 'power2.out'
        },
        '>+=2'
      )
      .to(
        idaMessage7,
        {
          opacity: 0,
          transform: 'translate(50px, 0)',
          duration: 0.5,
          ease: 'power2.out'
        },
        '<'
      )
      .to(idaMessage8, {
        opacity: 1,
        transform: 'translate(0, 0)',
        duration: 0.5,
        ease: 'power2.out'
      })
      .to(
        idaMessage8,
        {
          opacity: 0,
          transform: 'translate(50px, 0)',
          duration: 0.5,
          ease: 'power2.out'
        },
        '>+=2'
      )
      .to(
        clientMessage4,
        {
          opacity: 0,
          transform: 'translate(-50px, 0)',
          duration: 0.5,
          ease: 'power2.out'
        },
        '<'
      );
  };

  onMounted(() => {
    initBackgroundAnimation();
    initMessagesAnimation();

    animationLoaded.value = true;
  });

  return {
    animationRef,
    animationLoaded,
    mappedMessages
  };
};
